<script setup lang="ts">
import AppGrid from "@/components/AppGrid.vue";
import { FITTABLE_DEFAULT_COL_DEF } from "@/components/composables/useGridColumnDefaults.ts";
import DashboardStateView from "@/components/dashboard/DashboardStateView.vue";
import { urgencyLabel } from "@/features/results/resultHelpers.ts";
import type { PurchaseOrder } from "@/redistribution/models/PurchaseOrder.ts";
import type { Sku } from "@/redistribution/models/Sku.ts";
import { useRedistributionStore } from "@/stores/redistributionStore.ts";
import type { ColDef } from "ag-grid-community";
import { storeToRefs } from "pinia";
import { computed } from "vue";

const store = useRedistributionStore();
const { originalPOs, updatedPOs, redistributionResult } = storeToRefs(store);

// ── Helpers ───────────────────────────────────────────────────────────────────

function poMaxUrgency(po: PurchaseOrder): number {
  if (po.skus.length === 0) return 0;
  let maxUrgency = 0;
  for (const sku of po.skus) {
    if (sku.urgency > maxUrgency) {
      maxUrgency = sku.urgency;
    }
  }
  return maxUrgency;
}

function urgencyToBadgeClass(urgency: number, dailyRos: number): string {
  const label = urgencyLabel(urgency, dailyRos);
  switch (label.cssClass) {
    case "urgency-critical": {
      return "dashboard-status-badge--critical";
    }
    case "urgency-moderate": {
      return "dashboard-status-badge--moderate";
    }
    case "urgency-ok": {
      return "dashboard-status-badge--healthy";
    }
    default: {
      return "";
    }
  }
}

function wosDisplay(daysToStockout: number): string {
  const wos = daysToStockout / 7;
  return Number.isFinite(wos) ? `${wos.toFixed(1)}w` : "—";
}

// ── Section 1: PO-Level Health Summary ───────────────────────────────────────

interface UrgencyCounts {
  critical: number;
  moderate: number;
  healthy: number;
}

function countPOsByUrgency(pos: PurchaseOrder[]): UrgencyCounts {
  let critical = 0;
  let moderate = 0;
  let healthy = 0;
  for (const po of pos) {
    const u = poMaxUrgency(po);
    if (u >= 2) critical++;
    else if (u === 1) moderate++;
    else healthy++;
  }
  return { critical, moderate, healthy };
}

const beforeCounts = computed(() => countPOsByUrgency(originalPOs.value));
const afterCounts = computed(() => countPOsByUrgency(updatedPOs.value));

interface SummaryStatCard {
  label: string;
  emoji: string;
  before: number;
  after: number;
  /** Whether a decrease in count represents improvement */
  lowerIsBetter: boolean;
  badgeModifier: string;
}

const summaryCards = computed<SummaryStatCard[]>(() => [
  {
    label: "Critical POs",
    emoji: "🔴",
    before: beforeCounts.value.critical,
    after: afterCounts.value.critical,
    lowerIsBetter: true,
    badgeModifier: "dashboard-status-badge--critical",
  },
  {
    label: "Moderate Risk POs",
    emoji: "🟡",
    before: beforeCounts.value.moderate,
    after: afterCounts.value.moderate,
    lowerIsBetter: true,
    badgeModifier: "dashboard-status-badge--moderate",
  },
  {
    label: "Healthy POs",
    emoji: "🟢",
    before: beforeCounts.value.healthy,
    after: afterCounts.value.healthy,
    lowerIsBetter: false,
    badgeModifier: "dashboard-status-badge--healthy",
  },
]);

function deltaText(card: SummaryStatCard): string {
  const diff = card.after - card.before;
  if (diff === 0) return "";
  return diff > 0 ? `+${String(diff)}` : `\u2212${String(Math.abs(diff))}`;
}

function deltaColorClass(card: SummaryStatCard): string {
  const diff = card.after - card.before;
  if (diff === 0) return "dashboard-text-muted";
  const isImprovement = card.lowerIsBetter ? diff < 0 : diff > 0;
  return isImprovement ? "text-emerald-500" : "text-red-400";
}

// ── Section 2: SKU Drill-Down Grid ────────────────────────────────────────────

interface SkuGridRow {
  skuNumber: string;
  description: string;
  theme: string;
  daysToStockout: number;
  wos: number;
  urgency: number;
  lineItemQuantity: number;
  dailyRateOfSale: number;
}

const skuRowData = computed<SkuGridRow[]>(() => {
  const rows: SkuGridRow[] = [];
  for (const po of updatedPOs.value) {
    for (const sku of po.skus) {
      rows.push({
        skuNumber: sku.skuNumber,
        description: sku.description,
        theme: sku.theme,
        daysToStockout: sku.daysToStockout,
        wos: sku.daysToStockout / 7,
        urgency: sku.urgency,
        lineItemQuantity: sku.lineItemQuantity,
        dailyRateOfSale: sku.dailyRateOfSale,
      });
    }
  }
  return rows;
});

const columnDefs: ColDef[] = [
  {
    field: "skuNumber",
    headerName: "SKU",
    flex: 0.8,
    minWidth: 70,
    sortable: true,
    cellRenderer: (params: { value: string | undefined }) => {
      const el = document.createElement("strong");
      el.textContent = params.value ?? "";
      return el;
    },
  },
  {
    field: "description",
    headerName: "Description",
    flex: 2,
    minWidth: 120,
  },
  {
    field: "theme",
    headerName: "Collection",
    flex: 0.9,
    minWidth: 72,
  },
  {
    field: "wos",
    headerName: "WOS",
    flex: 0.6,
    minWidth: 60,
    sortable: true,
    valueFormatter: (params: { value: number | null | undefined }) =>
      params.value == null || !Number.isFinite(params.value)
        ? "—"
        : `${params.value.toFixed(1)}w`,
  },
  {
    field: "urgency",
    headerName: "Urgency",
    flex: 0.9,
    minWidth: 72,
    sortable: true,
    sort: "desc",
    cellRenderer: (params: {
      value: number | undefined;
      data?: SkuGridRow;
    }) => {
      const urgency = params.value ?? 0;
      const dailyRos = params.data?.dailyRateOfSale ?? 0;
      const label = urgencyLabel(urgency, dailyRos);
      const el = document.createElement("span");
      const modifier = (() => {
        switch (label.cssClass) {
          case "urgency-critical": {
            return "dashboard-status-badge--critical";
          }
          case "urgency-moderate": {
            return "dashboard-status-badge--moderate";
          }
          case "urgency-ok": {
            return "dashboard-status-badge--healthy";
          }
          default: {
            return "";
          }
        }
      })();
      el.className = ["dashboard-status-badge", modifier]
        .filter(Boolean)
        .join(" ");
      el.textContent = `${label.emoji} ${label.text}`;
      return el;
    },
  },
  {
    field: "daysToStockout",
    headerName: "Days to\nStockout",
    flex: 0.8,
    minWidth: 72,
    sortable: true,
  },
  {
    field: "lineItemQuantity",
    headerName: "Qty on PO",
    flex: 0.7,
    minWidth: 65,
    sortable: true,
  },
  {
    field: "dailyRateOfSale",
    headerName: "Daily Demand",
    flex: 0.8,
    minWidth: 72,
    valueFormatter: (params: { value: number | null | undefined }) =>
      params.value == null || !Number.isFinite(params.value)
        ? "—"
        : params.value.toFixed(2),
  },
];

const defaultColDef: ColDef = {
  ...FITTABLE_DEFAULT_COL_DEF,
  suppressMovable: true,
};

// ── Section 3: PO-Level Health Table ─────────────────────────────────────────

interface PoHealthRow {
  poNumber: string;
  skuCount: number;
  avgWos: number;
  worstUrgency: number;
  worstDailyRos: number;
  cubeTotal: number;
}

const poHealthRows = computed<PoHealthRow[]>(() =>
  updatedPOs.value.map((po) => {
    let worstSku: Sku | undefined;
    let totalWos = 0;

    for (const sku of po.skus) {
      if (!worstSku || sku.urgency > worstSku.urgency) {
        worstSku = sku;
      }
      totalWos += sku.daysToStockout / 7;
    }

    const avgWos = po.skus.length > 0 ? totalWos / po.skus.length : 0;

    return {
      poNumber: po.purchaseOrderNumber,
      skuCount: po.skus.length,
      avgWos,
      worstUrgency: worstSku?.urgency ?? 0,
      worstDailyRos: worstSku?.dailyRateOfSale ?? 0,
      cubeTotal: po.cubeTotal,
    };
  })
);

// ── PO Health Grid Column Definitions ─────────────────────────────────────────

const poHealthColumnDefs: ColDef[] = [
  {
    field: "poNumber",
    headerName: "PO Number",
    flex: 1.2,
    minWidth: 90,
    filter: "agTextColumnFilter",
    sort: "asc",
  },
  {
    field: "skuCount",
    headerName: "SKUs",
    flex: 0.6,
    minWidth: 60,
    filter: "agNumberColumnFilter",
  },
  {
    field: "avgWos",
    headerName: "Avg WOS",
    flex: 0.7,
    minWidth: 65,
    valueFormatter: (params: { value: number | undefined }) =>
      wosDisplay((params.value ?? 0) * 7),
  },
  {
    field: "worstUrgency",
    headerName: "Worst Urgency",
    flex: 1,
    minWidth: 90,
    cellRenderer: (params: { data: PoHealthRow }) => {
      const { worstUrgency, worstDailyRos } = params.data;
      const label = urgencyLabel(worstUrgency, worstDailyRos);
      const badgeClass = urgencyToBadgeClass(worstUrgency, worstDailyRos);
      const span = document.createElement("span");
      span.className = `dashboard-status-badge ${badgeClass}`;
      span.textContent = `${label.emoji} ${label.text}`;
      return span;
    },
  },
  {
    field: "cubeTotal",
    headerName: "Cube Total",
    flex: 0.8,
    minWidth: 72,
    filter: "agNumberColumnFilter",
    valueFormatter: (params: { value: number | undefined }) =>
      params.value != null && Number.isFinite(params.value)
        ? params.value.toFixed(1)
        : "—",
  },
];

const poHealthGridDefaultColDef: ColDef = FITTABLE_DEFAULT_COL_DEF;
</script>

<template>
  <div class="page-main-container gap-5">
    <!-- Page title and subtitle live in AppHeader (route meta). -->

    <!-- ── Empty State ─────────────────────────────────────────────────────── -->
    <DashboardStateView
      v-if="!redistributionResult"
      tone="empty"
      title="No redistribution data"
      message="Run PO Recommendations to view inventory health analysis."
    />

    <!-- ── Content ─────────────────────────────────────────────────────────── -->
    <template v-else>
      <!-- Section 1: Health Summary -->
      <section
        class="dashboard-card min-w-0 overflow-hidden rounded-[1.5rem] px-5 py-5"
      >
        <div class="flex items-center justify-between gap-3">
          <h2 class="dashboard-text-primary text-lg font-semibold">
            PO Health Summary
          </h2>
          <span class="dashboard-text-muted text-xs"
            >Before and after redistribution</span
          >
        </div>

        <div class="mt-5 grid gap-4 sm:grid-cols-3">
          <article
            v-for="card in summaryCards"
            :key="card.label"
            class="dashboard-card--inner rounded-2xl px-4 py-4"
          >
            <!-- Label -->
            <p
              class="dashboard-text-muted text-xs font-semibold tracking-[0.15em] uppercase"
            >
              {{ card.emoji }} {{ card.label }}
            </p>

            <!-- Before → After -->
            <div class="mt-3 flex items-end gap-3">
              <div class="flex flex-col">
                <span
                  class="dashboard-text-muted text-[0.65rem] tracking-wider uppercase"
                  >Before</span
                >
                <span
                  class="dashboard-text-primary text-3xl leading-none font-semibold"
                  >{{ card.before }}</span
                >
              </div>

              <span class="dashboard-text-muted mb-1 text-xl">›</span>

              <div class="flex flex-col">
                <span
                  class="dashboard-text-muted text-[0.65rem] tracking-wider uppercase"
                  >After</span
                >
                <span
                  class="dashboard-text-primary text-3xl leading-none font-semibold"
                  >{{ card.after }}</span
                >
              </div>

              <!-- Delta -->
              <span
                v-if="deltaText(card)"
                class="mb-1 text-sm font-semibold"
                :class="deltaColorClass(card)"
              >
                {{ deltaText(card) }}
              </span>
            </div>
          </article>
        </div>
      </section>

      <!-- Section 2: SKU Health Drill-Down Grid -->
      <section
        class="dashboard-card min-w-0 overflow-hidden rounded-[1.5rem] px-5 py-5"
      >
        <div class="mb-4 flex items-center justify-between gap-3">
          <h2 class="dashboard-text-primary text-lg font-semibold">
            SKU Health Drill-Down
          </h2>
          <span class="dashboard-text-muted text-xs">
            {{ skuRowData.length }} SKUs across {{ updatedPOs.length }} POs —
            sorted by urgency
          </span>
        </div>

        <AppGrid
          id="inventory-health-sku-grid"
          :column-defs="columnDefs"
          :row-data="skuRowData"
          :default-col-def="defaultColDef"
          dom-layout="autoHeight"
          :max-rows="50"
          :no-rows-overlay-component-params="{
            getTitle: () => 'No SKU data available',
          }"
          show-top-border
        />
      </section>

      <!-- Section 3: PO-Level Health Cards (collapsed view) -->
      <section
        class="dashboard-card min-w-0 overflow-hidden rounded-[1.5rem] px-5 py-5"
      >
        <div class="mb-4 flex items-center justify-between gap-3">
          <h2 class="dashboard-text-primary text-lg font-semibold">
            PO Health Overview
          </h2>
          <span class="dashboard-text-muted text-xs"
            >{{ poHealthRows.length }} purchase orders</span
          >
        </div>

        <div
          v-if="poHealthRows.length === 0"
          class="dashboard-text-muted py-6 text-center text-sm"
        >
          No PO data available.
        </div>

        <AppGrid
          v-else
          id="po-health-overview-grid"
          :column-defs="poHealthColumnDefs"
          :row-data="poHealthRows"
          :default-col-def="poHealthGridDefaultColDef"
          dom-layout="autoHeight"
          :max-rows="100"
          :no-rows-overlay-component-params="{
            getTitle: () => 'No PO data available',
          }"
          show-top-border
        />
      </section>
    </template>
  </div>
</template>
